import React, { useEffect, useState } from "react";
import DiaryList from "./DiaryList";
import axios from "axios";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import { FaPaw, FaPlus } from "react-icons/fa";
import { Diarydrawer } from "./Diarydrawer";
import config from "../../config";

const categories = [
  "General",
  "Medical",
  "Weight",
  "Temperature",
  "Water",
  "Food",
  "Potty",
  "Grooming",
  "Behaviour",
  "Other",
];

const UNIT_CATEGORIES = ["Temperature", "Weight", "Water"];
const UNIT_OPTIONS = {
  Temperature: ["°C", "°F"],
  Weight: ["kg", "gm"],
  Water: ["ml", "litre"],
};

export const Diary = () => {
  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : null;
  const client_id = decoded?.data?.customer_data?.id;

  const [entries, setEntries] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("General");
  const [editingEntry, setEditingEntry] = useState(null);

  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [pets, setPets] = useState([]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [activePet, setActivePet] = useState(null);

  const [unitValue, setUnitValue] = useState("");
  const [unitType, setUnitType] = useState("");
  const [type, setType] = useState("Public");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [docFiles, setDocFiles] = useState([]);
  const [showForm, setShowForm] = useState(true);

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState(new Date().toTimeString().slice(0, 5));
   
  const [selectedDiary, setSelectedDiary] = useState(null);
  const [originalEntry, setOriginalEntry] = useState(null);

  const [filterPet, setFilterPet] = useState(null); 
  const [isInitialized, setIsInitialized] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  /* -------------------- FETCH PETS -------------------- */
  const fetchPets = async () => {
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/customer/getallpet`,
        { client_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const petList = response?.data?.pets || [];
      setPets(petList);
    } catch {
      toast.error("Failed to fetch pets");
    }
  };

    useEffect(() => {
    if (pets.length > 0 && !activePet) {
      setActivePet(pets[0]._id);
    }
  }, [pets]);

  useEffect(() => {
    if (client_id && token) fetchPets();
  }, [client_id]);

  const hasChanges = React.useMemo(() => {
  if (!editingEntry || !originalEntry) return false;

  const originalPetId = originalEntry.petId?._id || originalEntry.petId;

  const currentNotes = UNIT_CATEGORIES.includes(selectedCategory)
    ? `${unitValue} ${unitType}`.trim()
    : notes.trim();

  const originalNotes = UNIT_CATEGORIES.includes(originalEntry.category)
    ? `${originalEntry.unitValue || ""} ${originalEntry.unitType || ""}`.trim()
    : (originalEntry.notes || "").trim();

  const isSameFiles = (uiFiles = [], backendFiles = []) => {
  const backendNormalized = backendFiles.map((file) =>
    typeof file === "string"
      ? file
      : file.s3Url || file.url
  );

  const uiNormalized = uiFiles.map((file) => file.url);
  return (
      uiNormalized.length === backendNormalized.length &&
      uiNormalized.every((url, i) => url === backendNormalized[i])
    );
  };

    const mediaChanged = !isSameFiles(mediaFiles, originalEntry.media);
    const docsChanged = !isSameFiles(docFiles, originalEntry.documents);

    return (
      selectedCategory !== (originalEntry.category || "") ||
      title !== (originalEntry.title || "") ||
      activePet !== originalPetId ||
      currentNotes !== originalNotes ||
      date !== (originalEntry.date?.slice(0, 10) || "") ||
      time !== (originalEntry.time || "") ||
      type !== (originalEntry.visibility || "").toLowerCase() ||
      mediaChanged || 
      docsChanged
    );
  }, [
    editingEntry,
    originalEntry,
    selectedCategory,
    title,
    activePet,
    notes,
    unitValue,
    unitType,
    date,
    time,
    type,
    mediaFiles, 
  docFiles,
  ]);

  const resetForm = () => {
  setEditingEntry(null); // Ensure we exit edit mode
  setOriginalEntry(null);
  setTitle("");
  setNotes("");
  setUnitValue("");
  setUnitType("");
  setMediaFiles([]);
  setDocFiles([]);
  setType("public");
  setDate(new Date().toISOString().slice(0, 10));
  setTime(new Date().toTimeString().slice(0, 5));
};

  /* -------------------- SAVE ENTRY -------------------- */
  const isSaveDisabled = UNIT_CATEGORIES.includes(selectedCategory)
    ? !unitValue || !unitType
    : !notes.trim();

  const normalizeFiles = (files = [], defaultType = 'unknown') => {
  return files.map(item => {
    if (typeof item === 'string') {
      const url = item;
      const rawName = url.split('/').pop()?.split('?')[0] || 'file';

      return {
        url,
        name: rawName,
        type: defaultType === 'document'
          ? 'document'
          : url.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/) 
            ? 'image'
            : url.toLowerCase().match(/\.(mp4|mov|webm)$/) 
              ? 'video'
              : 'unknown'
      };
    }
    return item; // already normalized object
  });
};

   /* -------------------- EDIT -------------------- */
  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setOriginalEntry(entry);
    setShowForm(true);
    setOpenDrawer(false);

    setSelectedCategory(entry.category || "General");
    setTitle(entry.title || "");
    setActivePet(entry.petId?._id || entry.petId || null);
    setType((entry.visibility || "public").toLowerCase()); // ← FIX 3 & 4
    setDate(entry.date ? entry.date.slice(0, 10) : new Date().toISOString().slice(0, 10));
    setTime(entry.time || new Date().toTimeString().slice(0, 5));
    setActivePet(entry.petId?._id || entry.petId || null);
    setMediaFiles(normalizeFiles(entry.media || []));
    setDocFiles(normalizeFiles(entry.documents || [], 'document'));
     

    if (UNIT_CATEGORIES.includes(entry.category)) {
      if (entry.unitValue && entry.unitType) {
        setUnitValue(entry.unitValue);
        setUnitType(entry.unitType);
      } else if (entry.notes) {
        const parts = entry.notes.split(" ");
        setUnitValue(parts[0] || "");
        setUnitType(parts[1] || "");
      }
      setNotes("");
    } else {
      setNotes(entry.notes || "");
      setUnitValue("");
      setUnitType("");
    }
  };

  /* -------------------- CANCEL EDIT -------------------- */
  const handleCancelEdit = () => {
    setEditingEntry(null);
    setShowForm(false);
    setSelectedCategory("General");        // consistent default
    setTitle("");
    setNotes("");
    setUnitValue("");
    setUnitType("");
    setMediaFiles([]);
    setDocFiles([]);
    setActivePet(null);
    setType("public");                     // ← lowercase
    setDate(new Date().toISOString().slice(0, 10));
    setTime(new Date().toTimeString().slice(0, 5));
    
  };

  const handleMediaUpload = async (file, uploadType) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("module", "Diary");
     // formData.append("docType", "media");
     formData.append("docType", uploadType === "media" ? "media" : "document");
      formData.append("customerId", client_id);

      const res = await axios.post(
        `${config.API_BASE_URL}/upload/fileuploadcustomer`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const { s3Url, s3Key } = res.data.data;
     
      const newFile = { 
        url: s3Url, 
        key: s3Key, 
        name: file.name || file.s3Url?.split("/").pop(),
        type: uploadType === "media" ? "image" : "document" 
      };
      if (uploadType === "media") {
      setMediaFiles((prev) => [...prev, newFile]);
    } else {
      setDocFiles((prev) => [...prev, newFile]);
    }
      //setMediaFiles((prev) => [
       // ...prev,
        //{ url: s3Url, key: s3Key, type: "image" },
      //]);
      toast.success("File uploaded");
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  useEffect(() => {
  if (entries.length === 0) {
    setShowForm(true);

    if (pets.length > 0) {
      setActivePet(pets[0]._id);
    } else {
      setActivePet(null);
    }

  } else {
    setShowForm(false);
  }
}, [entries, pets]);



  /* -------------------- SAVE -------------------- */
  const handleSave = async () => {
   // if (isSaveDisabled) return;
    if (!title?.trim()) {
    toast.error("Please enter a title");
    return;
  }
  if (isSaveDisabled) {
    toast.warn("Please fill in all required fields", { position: "top-center" });
    return;
  }

    const petId = activePet || pets[0]?._id;
    if (!petId) {
      toast.error("Please select a pet");
      return;
    }

    const petName = pets.find((p) => p._id === petId)?.name || "";

    const payload = {
      petId,
      petName,
      customerId: client_id,
      category: selectedCategory || "General",
      title,
      notes: UNIT_CATEGORIES.includes(selectedCategory)
        ? `${unitValue} ${unitType}`
        : notes,
      unitValue: UNIT_CATEGORIES.includes(selectedCategory) ? unitValue : null,
      unitType: UNIT_CATEGORIES.includes(selectedCategory) ? unitType : null,
      date,
      time,
      visibility: type,                   
      media: mediaFiles,
      documents: docFiles,
    };

    try {
      if (editingEntry) {
        const res = await axios.put(
          `${config.API_BASE_URL}/customer/updateDiary/${editingEntry._id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEntries((prev) =>
          prev.map((e) => (e._id === editingEntry._id ? res.data.data : e))
        );
        toast.success("Diary updated successfully");
      } else {
        const res = await axios.post(
          `${config.API_BASE_URL}/customer/addDairy`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEntries((prev) => [res.data.data, ...prev]);
        toast.success("Diary saved successfully");
      }

      // reset form
      setEditingEntry(null);
      setShowForm(false);
      setSelectedCategory("General");
      setTitle("");
      setNotes("");
      setUnitValue("");
      setUnitType("");
      setMediaFiles([]);
      setDocFiles([]);
      setType("");
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to save diary";
      toast.error(msg);   // ← FIX (real error message)
    }
  };

  /* -------------------- DELETE -------------------- */
  const handleDelete = async (diaryId) => {
    try {
      await axios.delete(
        `${config.API_BASE_URL}/customer/deleteDiary/${diaryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEntries((prev) => prev.filter((e) => e._id !== diaryId));
      toast.success("Diary deleted successfully");
    } catch {
      toast.error("Failed to delete diary");
    }
  };

  /* -------------------- FETCH DIARY -------------------- */
  const fetchDiaryList = async () => {
    if (!client_id) return;
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/customer/getDiary`,
        {
          params: { customerId: client_id },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data?.success) {
        setEntries(response.data.data || []);
       // if (response.data.count > 0) setShowForm(false);
      }
    } catch (error) {
      console.error("Failed to fetch diary:", error);
    }finally {
    // This is the key: The UI only "unlocks" now
    setIsInitialized(true); 
  }
  };

  useEffect(() => {
    fetchDiaryList();
  }, [client_id]);

  const filteredEntries = entries.filter((entry) => {
    const entryPetId = entry.petId?._id || entry.petId;
    return filterPet ? entryPetId === filterPet : true;
  });

    if (!isInitialized) {
      return <div className="min-h-screen bg-gray-50" />; 
    }

    const isFirstEntryFlow = entries.length === 0 && showForm;

  return (
    <div className="relative pb-20 lg:pb-0">
      <div className="space-y-6">
        {/* Pet Filter Header */}
        {entries.length > 0 && (
          <div className="bg-white rounded-2xl shadow-md p-5">
            {/* ... your existing pet filter UI (unchanged) ... */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
              <div className="diaryHeader">
                <h3 className="text-lg font-semibold text-blue-950 mb-3">Filter by Pet</h3>
                {pets.length > 0 ? (
                  <div className="flex gap-2 pb-2 scrollbar-thin scrollbar-thumb-gray-400 snap-x snap-mandatory lg:gap-3 lg:pb-0" style={{ overflow: "auto" }}>
                    <button
                        // Disable if the form is open (Adding or Editing)
                        disabled={showForm} 
                        onClick={() => setFilterPet(null)}
                        className={`flex items-center gap-2 px-5 py-3 rounded-full text-xs font-bold border-2 transition whitespace-nowrap ${
                          filterPet === null
                            ? "bg-orange-500 text-white border-orange-500 shadow-lg"
                            : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                        } ${showForm ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                      <FaPaw className="hidden lg:inline-block text-lg" /> All Pets
                    </button>
                    {pets.map((pet) => {
                        const isAddingForSpecificPet = showForm && !editingEntry && activePet === pet._id;
                          // 2. Determine if we are in "Edit Mode" for this pet
                        const isEditingThisPet = editingEntry && (editingEntry.petId?._id || editingEntry.petId) === pet._id; 
                        const isLockedToThisPet = isAddingForSpecificPet || isEditingThisPet;
                        const isDisabled = showForm && !isLockedToThisPet;
                        return (
                          <button
                            key={pet._id}
                            disabled={isDisabled}
                            onClick={() => setFilterPet(pet._id)}
                            className={`flex items-center gap-2 px-5 py-3 rounded-full text-xs font-bold border-2 transition whitespace-nowrap ${
                              filterPet === pet._id
                                ? "bg-orange-500 text-white border-orange-500 shadow-lg"
                                : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                            } ${
                              isDisabled ? "opacity-30 cursor-not-allowed grayscale" : "opacity-100"
                            } ${
                              showForm && isLockedToThisPet ? "border-orange-600 ring-2 ring-orange-200" : ""
                            }`}
                          >
                            <FaPaw className="hidden lg:inline-block text-lg" /> {pet.name}
                          </button>
                        );
                      })}
                  </div>
                ) : (
                  <p className="text-gray-500">No pets added yet</p>
                )}
              </div>

              <div className="hidden lg:block">
                <button
                  onClick={() => {
                    setSelectedDiary?.(null); // if you have this state
                    setOpenDrawer(true);
                  }}
                  className="px-6 py-3 rounded-2xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-lg font-bold hover:from-orange-600 hover:to-orange-700 transition shadow-xl whitespace-nowrap"
                >
                  ➕ Add New Entry
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 space-y-7">
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-blue-950">
              What's new with your pet today?
            </h2>

            {/* Categories */}
            <div>
              <label className="block text-lg font-semibold text-blue-950 mb-4">
                Category
              </label>
              <div className="flex flex-wrap gap-3">
                {(editingEntry ? [selectedCategory] : categories).map((cat) => (
                  <button
                    key={cat}
                   // onClick={() => setSelectedCategory(cat)}
                     onClick={() => {
                        if (!editingEntry && selectedCategory !== cat) {
                          resetForm();
                        }
                        setSelectedCategory(cat);
                      }}
                    className={`px-6 py-3 rounded-full text-base font-semibold transition shadow-sm ${
                      selectedCategory === cat
                        ? "bg-blue-950 text-white"
                        : "bg-gray-100 text-blue-900 hover:bg-gray-200"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Pet Selection */}
            <div>
              <label className="block text-lg font-semibold text-blue-950 mb-4">
                Who is this for?
              </label>
              {pets.length > 0 ? (
                <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-none snap-x snap-mandatory">
                  {pets
                    .filter((pet) => {
                        if (isFirstEntryFlow && !editingEntry) {
                          // Show ALL pets until user selects one
                          return true;
                        }

                        // After selection OR in edit mode → lock to active pet
                        return pet._id === activePet;
                      })
                    .map((pet) => {
                    const petImage = pet.profile_photo
                      ? pet.profile_photo.startsWith("http")
                        ? pet.profile_photo
                        : `${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${pet.profile_photo}`
                      : null;

                    const isSelectedInForm = activePet === pet._id;
                    return (
                      <div
                        key={pet._id}
                       // onClick={() => setActivePet(pet._id)}
                       onClick={() => {
                          if (editingEntry) return; // Prevent changing pet during edit
                          if (activePet && activePet !== pet._id) {
                            resetForm();
                          }

                          if (activePet !== pet._id) resetForm();
                          setActivePet(pet._id);
                        }}
                        className="flex flex-col items-center cursor-pointer snap-start flex-shrink-0"
                      >
                        <div
                          className={`w-24 h-24 rounded-full border-4 overflow-hidden shadow-lg transition-all ${
                            activePet === pet._id
                              ? "border-orange-500 scale-110"
                              : "border-gray-300"
                          }`}
                        >
                          {petImage ? (
                            <img
                              src={petImage}
                              alt={pet.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                              <span className="text-4xl font-bold text-gray-600">
                                {pet.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <span
                          className={`mt-3 text-lg font-bold ${
                            activePet === pet._id
                           
                              ? "text-orange-600"
                              : "text-gray-800"
                          }`}
                        >
                          {pet.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-lg">Please add a pet first</p>
              )}
            </div>

            {/* Title */}
             <div>
            <label className="block text-sm font-semibold text-blue-950 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Entry Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full text-xl px-6 py-4 rounded-2xl border-2 border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition"
            />
           </div>
            {/* Notes or Unit */}
            {UNIT_CATEGORIES.includes(selectedCategory) ? (
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Value Field */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-950 mb-2">
                  Value <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  placeholder={`Enter ${selectedCategory.toLowerCase()}`}
                  value={unitValue}
                  onChange={(e) => setUnitValue(e.target.value)}
                  className="w-full text-xl px-6 py-5 rounded-2xl border-2 border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition"
                />
            </div>
              {/* Unit Field */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-950 mb-2">
                  Unit <span className="text-red-500">*</span>
                </label>
                <select
                  value={unitType}
                  onChange={(e) => setUnitType(e.target.value)}
                  className="w-full text-xl px-6 py-5 rounded-2xl border-2 border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition"
                >
                  <option value="">Choose unit</option>
                  {UNIT_OPTIONS[selectedCategory]?.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            ) : (
               <div>
              <label className="block text-sm font-semibold text-blue-950 mb-2">
                Notes <span className="text-red-500">*</span>
              </label>
              <textarea
                placeholder="Write your notes here..."
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                className="w-full min-h-40 text-lg px-6 py-5 rounded-2xl border-2 border-gray-300 bg-gray-50 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none resize-none"
              />
              </div>
            )}

            {/* File Uploads */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="flex flex-col">
              <label className="block text-lg font-bold text-blue-950 mb-3">
                Photos / Videos
              </label>
              <div className="flex items-center gap-4">
                <label className="flex-shrink-0 flex items-center justify-center w-40 h-14 bg-blue-100 text-blue-800 rounded-2xl font-bold text-sm cursor-pointer hover:bg-blue-200 transition-colors active:scale-95">
                  <FaPlus className="mr-2" /> Add Media
                  <input
                    type="file"
                     multiple
                    accept="image/*,video/*"
                    className="hidden"
                  onChange={(e) => {
            const files = Array.from(e.target.files);
            files.forEach(file => handleMediaUpload(file, 'media'));
          }}
        />
     </label>               
        {/* Multiple Media Display */}
              <div className="ml-4 flex flex-wrap items-center gap-3 flex-1 min-w-0">
                {mediaFiles && mediaFiles.length > 0 ? (
                  mediaFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-2 bg-gray-100 p-2 rounded-xl border border-gray-200 group hover:border-blue-300 transition-all">
                      {/* Small Thumbnail */}
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                        {file?.url?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                          <img src={file.url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white text-[10px] font-bold">VID</div>
                        )}
                      </div>
                      
                      <div className="cursor-pointer" onClick={() => setPreviewFile(file)}>
                        <div className="text-xs font-medium text-blue-700 truncate max-w-[80px] hover:underline">
                          {file?.name || "Media"}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setMediaFiles(prev => prev.filter((_, i) => i !== index))}
                        className="text-red-500 hover:bg-red-100 p-1 rounded-full transition-colors"
                      >
                        <FaPlus className="rotate-45" size={12} />
                      </button>
                    </div>
                  ))
                ) : (
                  <span className="text-sm text-gray-400 italic font-medium">No file chosen</span>
                )}
              </div>
            </div>
          </div>
          {/* Documents Section */}
            <div className="flex flex-col">
              <label className="block text-lg font-bold text-blue-950 mb-3">
                Documents
              </label>
              <div className="flex items-center gap-4">
                {/* Add Document Button */}
                <label className="flex-shrink-0 flex items-center justify-center w-40 h-14 bg-orange-100 text-orange-800 rounded-2xl font-bold text-sm cursor-pointer hover:bg-orange-200 transition-colors active:scale-95">
                  <FaPlus className="mr-2" /> Add Document
                  <input
                    type="file"
                     multiple
                    accept=".pdf,.doc,.docx,.txt"
                    className="hidden"
                    onChange={(e) => {
                    const files = Array.from(e.target.files);
                    files.forEach(file => handleMediaUpload(file, 'doc'));
                  }}
               />
            </label>
        {/* Multiple Document Display */}
        <div className="ml-4 flex flex-wrap items-center gap-3 flex-1 min-w-0">
          {docFiles && docFiles.length > 0 ? (
            docFiles.map((file, index) => (
              <div key={index} className="flex items-center gap-2 bg-orange-50 p-2 rounded-xl border border-orange-200 group hover:border-orange-400 transition-all">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0 text-xl">
                  📄
                </div>
                
                <div className="cursor-pointer" onClick={() => {
                  const urlLower = file.url?.toLowerCase();
                  if (urlLower?.match(/\.(jpg|jpeg|png|gif|webp|mp4|mov|webm)$/i)) {
                    setPreviewFile(file);
                  } else {
                    window.open(file.url, '_blank');
                  }
                }}>
                  <div className="text-xs font-medium text-orange-700 truncate max-w-[80px] hover:underline">
                    {file?.name || "Doc"}
                  </div>
                </div>
                    <button
                      type="button"
                      onClick={() => setDocFiles(prev => prev.filter((_, i) => i !== index))}
                      className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition-colors"
                    >
                      <FaPlus className="rotate-45" size={12} />
                    </button>
                  </div>
                ))
              ) : (
                <span className="text-sm text-gray-500 italic">No document chosen</span>
                )}
              </div>
            </div>
          </div>
        </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-lg font-bold text-blue-950 mb-3">
                  Date
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full text-lg px-6 py-5 rounded-2xl border-2 border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
                />
              </div>
              <div>
                <label className="block text-lg font-bold text-blue-950 mb-3">
                  Time
                </label>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full text-lg px-6 py-5 rounded-2xl border-2 border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
                />
              </div>
            </div>

            {/* Visibility */}
            <div>
            <label className="block text-sm font-semibold text-gray-700 mb-4">
              Visibility
              <span className="ml-2 text-xs text-gray-500">
                (Public = visible to service provider)
              </span>
            </label>
            <div className="flex gap-4">
              {["public", "private"].map((v) => (
                <button
                  key={v}
                  onClick={() => setType(v)}
                  className={`flex-1 py-3.5 rounded-xl font-bold text-base transition shadow-md ${
                    type === v
                      ? "bg-orange-500 text-white"
                      : "bg-orange-100 text-orange-700 hover:bg-orange-200"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          

            <div className="pt-8 border-t-2 border-gray-200 flex justify-end gap-8">
              {(editingEntry || (showForm && entries.length > 0)) && (
                <button
                  onClick={handleCancelEdit}
                  className="px-10 py-5 rounded-2xl text-xl font-bold bg-gray-200 text-gray-800 hover:bg-gray-300 transition shadow-lg"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={editingEntry ? !hasChanges : isSaveDisabled}
                className={`inline-block px-10 py-5 rounded-2xl text-xl font-bold transition shadow-2xl ${
                  (editingEntry ? !hasChanges : isSaveDisabled)
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-600 to-indigo-700 text-white hover:from-blue-700 hover:to-indigo-800"
                }`}
              >
                {editingEntry ? "Update Entry" : "Save Entry"}
              </button>
            </div>
          </div>
        )}       
      {previewFile && (
      <div
        className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
        onClick={() => setPreviewFile(null)}
      >
        <div
          className="relative bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2.5 transition"
            onClick={() => setPreviewFile(null)}
          >
            ✕
          </button>

          {/* Preview content */}
              {previewFile.url?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                <img
                  src={previewFile.url}
                  alt={previewFile.name || "Preview"}
                  className="w-full max-h-[85vh] object-contain mx-auto"
                />
              ) : previewFile.url?.toLowerCase().match(/\.(mp4|webm|mov)$/i) ? (
                <video
                  src={previewFile.url}
                  controls
                  autoPlay
                  className="w-full max-h-[85vh] mx-auto"
                />
                  ) : (
                    <div className="p-12 text-center text-gray-700">
                      <p className="text-lg mb-4">Preview not supported for this file type</p>
                      <a
                        href={previewFile.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline hover:text-blue-800"
                      >
                        Open in new tab
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

        {/* Diary List */}
        <div
          key={filterPet || 'all'} 
          className="animate-fadeIn transition-opacity duration-300"
        >
          {filteredEntries.length > 0 && !showForm ? (
            <DiaryList
              entries={filteredEntries}
              pets={pets}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ) : (
            !showForm && (
              <div className="flex flex-col items-center justify-center py-20 px-6 bg-white rounded-3xl shadow-sm border-2 border-dashed border-gray-200">
                <div className="w-24 h-24 bg-orange-50 rounded-full flex items-center justify-center mb-6">
                  <FaPaw className="text-4xl text-orange-400 opacity-50" />
                </div>
                <h3 className="text-2xl font-bold text-blue-950 mb-2 text-center">
                  No diary entries found
                </h3>
                <p className="text-gray-500 text-center max-w-md mb-8">
                  {filterPet 
                    ? `It looks like you haven't recorded any moments for ${pets.find(p => p._id === filterPet)?.name || "this pet"} yet.`
                    : "Your pet diary is currently empty. Start capturing your furry friend's journey today!"}
                </p>
                <button
                  onClick={() => {
                    resetForm();
                    setShowForm(true);
                    if (filterPet) setActivePet(filterPet);
                  }}
                  className="flex items-center gap-2 px-8 py-4 bg-blue-950 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg hover:shadow-blue-200 active:scale-95"
                >
                  <FaPlus /> Create First Entry
                </button>
              </div>
            )
          )}
        </div>
        {/* Floating Action Button - Only on mobile & tablet */}
        <button
          onClick={() => setOpenDrawer(true)}
          className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full shadow-2xl flex items-center justify-center text-3xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300 hover:scale-110 lg:hidden"
          aria-label="Add new entry"
        >
          <FaPlus />
        </button>

        <Diarydrawer
          open={openDrawer}
          onClose={() => {
            setOpenDrawer(false);
            // setSelectedDiary(null); // uncomment if you use this state
          }}
          pets={pets}
          diaryToEdit={null} // or your selectedDiary state
          onSaveSuccess={fetchDiaryList}
        />
      </div>
    </div>
  );
};
